import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Loader2, Search, Award, Sparkles, TrendingUp, Quote, ChevronDown,
  Heart, Wrench, Truck, Sparkle as SparkIcon, Bug, Car, Hammer, HandHeart, Briefcase,
  Landmark, ShieldCheck, BarChart3, Building2, Coins, Cpu, AlertTriangle, Brain,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { loadContent } from '../lib/contentLoader';
import useCanSeeCounts from '../hooks/useCanSeeCounts';

// ── Visuals: blue-collar categories ─────────────────────────────────────
const CATEGORY_VISUALS = {
  'Beauty & Wellness': { icon: Heart, dot: 'bg-pink-500', text: 'text-pink-700', bg: 'bg-pink-50', solid: 'bg-pink-600' },
  'Appliance & Device Repair': { icon: Wrench, dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', solid: 'bg-blue-600' },
  'Delivery & Logistics': { icon: Truck, dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', solid: 'bg-amber-600' },
  'Home Cleaning & Hygiene': { icon: SparkIcon, dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', solid: 'bg-emerald-600' },
  'Pest Management': { icon: Bug, dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', solid: 'bg-orange-600' },
  'Transport & Mobility': { icon: Car, dot: 'bg-indigo-500', text: 'text-indigo-700', bg: 'bg-indigo-50', solid: 'bg-indigo-600' },
  'Home Repair & Improvement': { icon: Hammer, dot: 'bg-stone-500', text: 'text-stone-700', bg: 'bg-stone-50', solid: 'bg-stone-600' },
  'Home Support & Care': { icon: HandHeart, dot: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', solid: 'bg-rose-600' },
  'Skilled Service Career': { icon: Briefcase, dot: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50', solid: 'bg-purple-600' },
};
const DEFAULT_VIS = { icon: Briefcase, dot: 'bg-gray-500', text: 'text-gray-700', bg: 'bg-gray-50', solid: 'bg-gray-600' };
const getVis = (cat) => CATEGORY_VISUALS[cat] || DEFAULT_VIS;

// ── Visuals: banking role families ───────────────────────────────────────
const BANKING_FAMILY_VISUALS = {
  'Government / Public Sector Banking': { icon: Landmark, text: 'text-blue-800', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-600' },
  'Private / Retail Banking':           { icon: Building2, text: 'text-indigo-800', bg: 'bg-indigo-50', border: 'border-indigo-200', dot: 'bg-indigo-600' },
  'Commercial / Corporate Banking':     { icon: Briefcase, text: 'text-slate-800', bg: 'bg-slate-100', border: 'border-slate-300', dot: 'bg-slate-600' },
  'Investment Banking / Capital Markets':{ icon: BarChart3, text: 'text-emerald-800', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-600' },
  'Wealth / Private Banking':           { icon: Coins, text: 'text-amber-800', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-600' },
  'Risk / Compliance / Fraud':          { icon: ShieldCheck, text: 'text-rose-800', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-600' },
  'Operations / Back Office':           { icon: HandHeart, text: 'text-cyan-800', bg: 'bg-cyan-50', border: 'border-cyan-200', dot: 'bg-cyan-600' },
  'Technology / Data / Digital Banking':{ icon: Cpu, text: 'text-violet-800', bg: 'bg-violet-50', border: 'border-violet-200', dot: 'bg-violet-600' },
  'AI / Automation / Governance':       { icon: Brain, text: 'text-teal-800', bg: 'bg-teal-50', border: 'border-teal-200', dot: 'bg-teal-600' },
};
const DEFAULT_BANK_VIS = { icon: Landmark, text: 'text-blue-800', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-600' };
const getBankVis = (fam) => BANKING_FAMILY_VISUALS[fam] || DEFAULT_BANK_VIS;

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const riskTone = (summary = '') => {
  const s = summary.toLowerCase();
  if (s.includes('high')) return { label: 'HIGH AI RISK', bg: 'bg-rose-100', text: 'text-rose-800', dot: 'bg-rose-500' };
  if (s.includes('medium')) return { label: 'MEDIUM AI RISK', bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' };
  return { label: 'LOW AI RISK', bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' };
};

const CareersAPPage = () => {
  const canSeeCounts = useCanSeeCounts();
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();

  // Sector: null (picker) | 'blue-collar' | 'banking'
  const [sector, setSector] = useState(null);

  // Blue-collar state
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [query, setQuery] = useState('');

  // Banking state
  const [bankingPkg, setBankingPkg] = useState(null);
  const [bankingLoading, setBankingLoading] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [bankingQuery, setBankingQuery] = useState('');

  useEffect(() => {
    loadContent('skilled_service_careers.json')
      .then((d) => {
        if (d && !d.__locked) setPkg(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Lazy-load banking JSON when the user opens that sector
  useEffect(() => {
    if (sector === 'banking' && !bankingPkg && !bankingLoading) {
      setBankingLoading(true);
      loadContent('banking_roles.json')
        .then((d) => {
          if (d && !d.__locked) setBankingPkg(d);
          setBankingLoading(false);
        })
        .catch(() => setBankingLoading(false));
    }
  }, [sector, bankingPkg, bankingLoading]);

  // Deep-link: /explore/careers-ts-ap/:category goes straight to blue-collar
  useEffect(() => {
    if (urlCategory && pkg) {
      const match = pkg.careers.find(c => slugify(c.category) === urlCategory);
      if (match) { setSector('blue-collar'); setSelectedCategory(match.category); }
    }
  }, [urlCategory, pkg]);

  useEffect(() => { setQuery(''); window.scrollTo(0, 0); }, [selectedCategory]);
  useEffect(() => { setBankingQuery(''); window.scrollTo(0, 0); }, [selectedFamily]);
  useEffect(() => { window.scrollTo(0, 0); }, [sector]);

  const categories = useMemo(() => {
    if (!pkg) return [];
    const map = {};
    pkg.careers.forEach(c => {
      if (!map[c.category]) map[c.category] = { items: [], platforms: new Set() };
      map[c.category].items.push(c);
      if (c.platform) map[c.category].platforms.add(c.platform);
    });
    return Object.entries(map).map(([cat, { items, platforms }]) => ({
      name: cat,
      count: items.length,
      sampleSymbols: items.slice(0, 4).map(it => it.symbol).filter(Boolean),
      platforms: [...platforms].slice(0, 3),
      visual: getVis(cat),
    })).sort((a, b) => b.count - a.count);
  }, [pkg]);

  const categoryCareers = useMemo(() => {
    if (!pkg || !selectedCategory) return [];
    const all = pkg.careers.filter(c => c.category === selectedCategory);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q) ||
      c.platform?.toLowerCase().includes(q)
    );
  }, [pkg, selectedCategory, query]);

  const bankingFamilies = useMemo(() => {
    if (!bankingPkg) return [];
    const map = {};
    bankingPkg.roles.forEach(r => {
      if (!map[r.role_family]) map[r.role_family] = [];
      map[r.role_family].push(r);
    });
    return Object.entries(map).map(([fam, items]) => ({
      name: fam,
      count: items.length,
      sampleRoles: items.slice(0, 3).map(r => r.role_name),
      visual: getBankVis(fam),
    })).sort((a, b) => b.count - a.count);
  }, [bankingPkg]);

  const familyRoles = useMemo(() => {
    if (!bankingPkg || !selectedFamily) return [];
    const all = bankingPkg.roles.filter(r => r.role_family === selectedFamily);
    const q = bankingQuery.trim().toLowerCase();
    if (!q) return all;
    return all.filter(r => r.role_name.toLowerCase().includes(q) || r.daily_work_preview?.toLowerCase().includes(q));
  }, [bankingPkg, selectedFamily, bankingQuery]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50"><Loader2 className="animate-spin text-amber-600" size={32} /></div>;
  if (!pkg) return <div className="min-h-screen flex items-center justify-center"><p>Could not load careers.</p></div>;

  const totalBlue = pkg.careers.length;
  const totalBank = bankingPkg?.roles?.length || 120;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="careers-ts-ap-page">
      <Navbar />

      {/* ────────────────────────────────────────────────────────────────
          STAGE 0 — Sector picker (two vertically-stacked panels)
         ──────────────────────────────────────────────────────────────── */}
      {sector === null && (
        <>
          <section className="pt-28 pb-10 px-4 md:px-6 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(251,146,60,0.2) 1px, transparent 1px), radial-gradient(circle at 80% 60%, rgba(251,146,60,0.2) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="max-w-4xl mx-auto text-center relative">
              <Badge className="bg-amber-100 text-amber-800 mb-3 font-bold">Telangana &amp; AP · Two career worlds, one place</Badge>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">Pick your career world</h1>
              <p className="text-gray-700 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
                {canSeeCounts
                  ? `${totalBlue} skilled-service trades on one side. ${totalBank} banking roles on the other.`
                  : 'Skilled-service trades on one side. Banking roles on the other.'}
                {' '}Open the world that fits where you&apos;re heading.
              </p>
            </div>
          </section>

          {/* Two stacked sector tiles — TOP and BOTTOM, not side by side */}
          <section className="px-4 md:px-6 py-10">
            <div className="max-w-5xl mx-auto flex flex-col gap-6" data-testid="sector-picker">

              {/* Blue-Collar tile */}
              <button
                type="button"
                onClick={() => setSector('blue-collar')}
                data-testid="sector-blue-collar"
                className="group relative text-left bg-white rounded-3xl border-2 border-amber-200 hover:border-amber-500 hover:shadow-2xl transition-all overflow-hidden p-6 md:p-8"
              >
                <div aria-hidden className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-100 opacity-60 group-hover:opacity-90 transition-opacity" />
                <div aria-hidden className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-orange-100 opacity-50 group-hover:opacity-80 transition-opacity" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow-md shrink-0">
                    <Hammer size={30} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-amber-100 text-amber-800 border-0 mb-2 font-bold">{canSeeCounts ? `${totalBlue} ROLES · 9 CATEGORIES` : 'SKILLED-SERVICE CAREERS'}</Badge>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-2">
                      Blue-Collar Job Role Previews
                    </h2>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed max-w-2xl">
                      Skilled-trade careers powering Telangana &amp; AP every day — Beauty, Repair, Delivery, Cleaning, Transport, Home Repair, Pest Management, Home Support &amp; more. AI-resistant, real-world, dignity-first.
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {['Beauty & Wellness', 'Appliance Repair', 'Delivery & Logistics', 'Home Cleaning', 'Pest Management'].map((t) => (
                        <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded bg-white border border-amber-200 text-amber-800">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="hidden md:flex flex-col items-end gap-2">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-amber-600 group-hover:bg-amber-700 text-white font-semibold rounded-full transition-colors shadow-md">
                      Open <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
                <div className="relative mt-5 pt-5 border-t border-amber-100 md:hidden">
                  <span className="inline-flex items-center gap-1.5 px-5 py-2 bg-amber-600 text-white font-semibold rounded-full text-sm">
                    Open <ArrowRight size={14} />
                  </span>
                </div>
              </button>

              {/* Banking tile */}
              <button
                type="button"
                onClick={() => setSector('banking')}
                data-testid="sector-banking"
                className="group relative text-left bg-white rounded-3xl border-2 border-blue-200 hover:border-blue-500 hover:shadow-2xl transition-all overflow-hidden p-6 md:p-8"
              >
                <div aria-hidden className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-blue-100 opacity-60 group-hover:opacity-90 transition-opacity" />
                <div aria-hidden className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-indigo-100 opacity-50 group-hover:opacity-80 transition-opacity" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-blue-700 text-white flex items-center justify-center shadow-md shrink-0">
                    <Landmark size={30} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-blue-100 text-blue-800 border-0 mb-2 font-bold">{canSeeCounts ? `${totalBank} ROLES · 9 FAMILIES` : 'BANKING CAREERS'}</Badge>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-2">
                      Banking Job Role Previews
                    </h2>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed max-w-2xl">
                      Government PSU banking · Private &amp; Retail · Corporate · Investment · Wealth · Risk &amp; Compliance · Operations · Technology · AI Governance. Verified eligibility, daily work, AI risk &amp; readiness quiz for each role.
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {['SBI / IBPS PO', 'RBI Grade B', 'Relationship Manager', 'Risk Analyst', 'AI Model Validator'].map((t) => (
                        <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-800">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="hidden md:flex flex-col items-end gap-2">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-blue-700 group-hover:bg-blue-800 text-white font-semibold rounded-full transition-colors shadow-md">
                      Open <ArrowRight size={16} />
                    </span>
                  </div>
                </div>
                <div className="relative mt-5 pt-5 border-t border-blue-100 md:hidden">
                  <span className="inline-flex items-center gap-1.5 px-5 py-2 bg-blue-700 text-white font-semibold rounded-full text-sm">
                    Open <ArrowRight size={14} />
                  </span>
                </div>
              </button>

            </div>
          </section>

          <section className="px-4 md:px-6 pb-16">
            <div className="max-w-4xl mx-auto p-6 bg-white border border-gray-200 rounded-2xl">
              <div className="flex items-start gap-3">
                <Quote className="text-amber-600 flex-shrink-0 mt-1" size={22} />
                <div>
                  <p className="text-sm md:text-base text-gray-800 font-medium leading-relaxed mb-1">
                    &quot;Every career listed here — from a Beauty Technician to a Probationary Officer — builds dignity, real-world trust, and long-term growth.&quot;
                  </p>
                  <p className="text-xs text-gray-500">No work is small. These trades and roles power Telangana &amp; AP every single day.</p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ────────────────────────────────────────────────────────────────
          STAGE 1 — Blue-Collar flow (existing 173 roles)
         ──────────────────────────────────────────────────────────────── */}
      {sector === 'blue-collar' && !selectedCategory && (
        <>
          <section className="pt-28 pb-12 px-4 md:px-6 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(251,146,60,0.2) 1px, transparent 1px), radial-gradient(circle at 80% 60%, rgba(251,146,60,0.2) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="max-w-4xl mx-auto text-center relative">
              <button onClick={() => setSector(null)} data-testid="back-to-sectors-bc" className="inline-flex items-center gap-1.5 text-sm text-amber-800 hover:text-amber-900 mb-3 bg-white/70 px-3 py-1.5 rounded-full border border-amber-200">
                <ArrowLeft size={14} /> Back to career worlds
              </button>
              <Badge className="bg-amber-100 text-amber-800 mb-3 font-bold">Telangana &amp; AP · {canSeeCounts ? `${totalBlue} Blue-Collar Job Roles` : 'Blue-Collar Job Roles'}</Badge>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">{pkg.pageTitle}</h1>
              <p className="text-gray-700 max-w-3xl mx-auto text-sm md:text-base leading-relaxed mb-5">{pkg.intro}</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full border border-amber-200">
                <Award size={16} className="text-amber-600" />
                <span className="text-xs font-bold text-amber-900">AI-Resistant · Real-World · Trusted Trades</span>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10">
            <div className="max-w-6xl mx-auto">
              {canSeeCounts && <p className="text-xs text-gray-500 mb-4">Showing <strong>{categories.length}</strong> career categories · <strong>{totalBlue}</strong> roles total</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="category-grid">
                {categories.map(cat => {
                  const Icon = cat.visual.icon;
                  return (
                    <button
                      key={cat.name}
                      data-testid={`category-tile-${slugify(cat.name)}`}
                      onClick={() => setSelectedCategory(cat.name)}
                      className="group relative text-left p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-gray-400 hover:shadow-lg transition-all overflow-hidden"
                    >
                      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${cat.visual.bg} opacity-60 group-hover:opacity-90 transition-opacity`} />
                      <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl ${cat.visual.bg} ${cat.visual.text} mb-3`}>
                        <Icon size={22} />
                      </div>
                      <h3 className="relative text-base md:text-lg font-bold text-gray-900 mb-2 pr-6 leading-snug">{cat.name}</h3>
                      {cat.sampleSymbols.length > 0 && (
                        <div className="relative flex gap-1 mb-3 text-lg">
                          {cat.sampleSymbols.map((s, i) => <span key={i}>{s}</span>)}
                        </div>
                      )}
                      <div className="relative flex items-center justify-between">
                        <Badge className={`${cat.visual.bg} ${cat.visual.text} border-0 text-[11px] font-bold`}>{canSeeCounts ? `${cat.count} careers` : 'Explore'}</Badge>
                        <ArrowRight size={16} className="text-gray-400 group-hover:text-gray-700 group-hover:translate-x-1 transition-all" />
                      </div>
                      {cat.platforms.length > 0 && (
                        <p className="relative text-[10px] text-gray-500 mt-2">via {cat.platforms.join(', ')}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      )}

      {sector === 'blue-collar' && selectedCategory && (
        <>
          <section className="pt-28 pb-10 px-4 md:px-6 bg-gradient-to-br from-amber-600 via-orange-600 to-rose-600 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, white 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
            <div className="max-w-5xl mx-auto relative">
              <button onClick={() => { setSelectedCategory(null); navigate('/explore/careers-ts-ap'); }} data-testid="back-to-categories" className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white mb-4 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <ArrowLeft size={14} /> Back to all categories
              </button>
              <Badge className="bg-white text-amber-700 border-0 font-bold mb-2">Telangana &amp; AP</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{selectedCategory}</h1>
              <p className="text-white/90 text-sm md:text-base">{categoryCareers.length} skilled careers · respected daily across Telangana &amp; Andhra Pradesh</p>
            </div>
          </section>

          <section className="px-4 md:px-6 py-6 bg-white border-b border-gray-200">
            <div className="max-w-5xl mx-auto">
              <div className="relative max-w-xl">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input data-testid="category-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${selectedCategory} careers...`} className="pl-9" />
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-4" data-testid="careers-grid">
                {categoryCareers.map((c, i) => <CareerCard key={c.id} career={c} index={i + 1} onOpen={() => navigate(`/explore/careers-ts-ap/blue-collar/${c.id}`)} />)}
              </div>
              {categoryCareers.length === 0 && <p className="text-center text-sm text-gray-500 py-8">No careers match your search.</p>}
            </div>
          </section>
        </>
      )}

      {/* ────────────────────────────────────────────────────────────────
          STAGE 2 — Banking flow
         ──────────────────────────────────────────────────────────────── */}
      {sector === 'banking' && bankingLoading && (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
          <Loader2 className="animate-spin text-blue-700" size={32} />
        </div>
      )}

      {sector === 'banking' && bankingPkg && !selectedFamily && (
        <>
          <section className="pt-28 pb-12 px-4 md:px-6 bg-gradient-to-br from-blue-50 via-indigo-50 to-slate-50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.18) 1px, transparent 1px), radial-gradient(circle at 80% 60%, rgba(99,102,241,0.18) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="max-w-4xl mx-auto text-center relative">
              <button onClick={() => setSector(null)} data-testid="back-to-sectors-bank" className="inline-flex items-center gap-1.5 text-sm text-blue-800 hover:text-blue-900 mb-3 bg-white/70 px-3 py-1.5 rounded-full border border-blue-200">
                <ArrowLeft size={14} /> Back to career worlds
              </button>
              <Badge className="bg-blue-100 text-blue-800 mb-3 font-bold">Banking sector · {canSeeCounts ? `${totalBank} job roles` : 'curated roles'}</Badge>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">Banking Job Role Previews</h1>
              <p className="text-gray-700 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
                Government PSU banking, private retail, corporate, investment, wealth, risk &amp; compliance, operations, tech &amp; AI governance — verified eligibility, daily work, AI risk and readiness quiz for each role.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full border border-blue-200">
                <AlertTriangle size={14} className="text-amber-600" />
                <span className="text-[11px] font-semibold text-blue-900">
                  Always read the latest official notification before applying
                </span>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10">
            <div className="max-w-6xl mx-auto">
              {canSeeCounts && <p className="text-xs text-gray-500 mb-4">Showing <strong>{bankingFamilies.length}</strong> role families · <strong>{totalBank}</strong> banking roles total</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="banking-family-grid">
                {bankingFamilies.map((fam) => {
                  const Icon = fam.visual.icon;
                  return (
                    <button
                      key={fam.name}
                      data-testid={`banking-family-${slugify(fam.name)}`}
                      onClick={() => setSelectedFamily(fam.name)}
                      className={`group relative text-left p-5 bg-white rounded-2xl border-2 ${fam.visual.border} hover:border-blue-500 hover:shadow-xl transition-all overflow-hidden`}
                    >
                      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${fam.visual.bg} opacity-60 group-hover:opacity-90 transition-opacity`} />
                      <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl ${fam.visual.bg} ${fam.visual.text} mb-3`}>
                        <Icon size={22} />
                      </div>
                      <h3 className="relative text-base md:text-lg font-bold text-gray-900 mb-2 pr-6 leading-snug">{fam.name}</h3>
                      <ul className="relative text-[11px] text-gray-600 mb-3 space-y-0.5">
                        {fam.sampleRoles.slice(0, 3).map((r) => (
                          <li key={r} className="flex items-start gap-1.5">
                            <span className={`mt-1 w-1 h-1 rounded-full ${fam.visual.dot} shrink-0`} /> {r}
                          </li>
                        ))}
                      </ul>
                      <div className="relative flex items-center justify-between pt-2 border-t border-gray-100">
                        <Badge className={`${fam.visual.bg} ${fam.visual.text} border-0 text-[11px] font-bold`}>{canSeeCounts ? `${fam.count} roles` : 'Explore'}</Badge>
                        <ArrowRight size={16} className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {bankingPkg.important_safety_note && (
            <section className="px-4 md:px-6 pb-12">
              <div className="max-w-4xl mx-auto p-5 bg-blue-50 border-l-4 border-blue-500 rounded-r-2xl flex gap-3">
                <AlertTriangle className="text-blue-700 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-1">Important safety note</p>
                  <p className="text-sm text-blue-900 leading-relaxed">{bankingPkg.important_safety_note}</p>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {sector === 'banking' && bankingPkg && selectedFamily && (
        <>
          <section className="pt-28 pb-10 px-4 md:px-6 bg-gradient-to-br from-blue-700 via-indigo-700 to-slate-800 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, white 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
            <div className="max-w-5xl mx-auto relative">
              <button onClick={() => setSelectedFamily(null)} data-testid="back-to-banking-families" className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white mb-4 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <ArrowLeft size={14} /> Back to banking families
              </button>
              <Badge className="bg-white text-blue-800 border-0 font-bold mb-2">Banking sector</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{selectedFamily}</h1>
              <p className="text-white/90 text-sm md:text-base">{familyRoles.length} verified roles · eligibility, daily work, AI risk and readiness quiz</p>
            </div>
          </section>

          <section className="px-4 md:px-6 py-6 bg-white border-b border-gray-200">
            <div className="max-w-5xl mx-auto">
              <div className="relative max-w-xl">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input data-testid="banking-search-input" value={bankingQuery} onChange={(e) => setBankingQuery(e.target.value)} placeholder={`Search ${selectedFamily}…`} className="pl-9" />
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-4" data-testid="banking-roles-grid">
                {familyRoles.map((r) => <BankingRoleCard key={r.catalog_number} role={r} onOpen={() => navigate(`/explore/careers-ts-ap/banking/${r.catalog_number}`)} />)}
              </div>
              {familyRoles.length === 0 && <p className="text-center text-sm text-gray-500 py-8">No banking roles match your search.</p>}
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

// ── Blue-collar career card ──────────────────────────────────────────────
const CareerCard = ({ career, index, onOpen }) => {
  const v = getVis(career.category);
  return (
    <motion.button data-testid={`career-card-${career.id}`} onClick={onOpen} whileHover={{ y: -3 }} transition={{ duration: 0.2 }}
      className="text-left p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-gray-400 hover:shadow-lg transition-all flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className={`flex-shrink-0 w-12 h-12 rounded-xl ${v.bg} ${v.text} flex items-center justify-center text-2xl`}>{career.symbol || '•'}</span>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-wider ${v.text} mb-0.5`}>#{String(index).padStart(2, '0')} · {career.platform}</p>
          <h3 className="text-base font-bold text-gray-900 leading-tight">{career.title}</h3>
        </div>
      </div>
      {career.description && <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{career.description}</p>}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className={`text-[11px] font-semibold ${v.text} inline-flex items-center gap-1`}><Sparkles size={11} />Read full preview</span>
        <ArrowRight size={14} className={`${v.text}`} />
      </div>
    </motion.button>
  );
};

// ── Banking role card ────────────────────────────────────────────────────
const BankingRoleCard = ({ role, onOpen }) => {
  const v = getBankVis(role.role_family);
  const Icon = v.icon;
  const tone = riskTone(role.ai_risk_and_staying_relevant?.ai_risk_summary || '');
  return (
    <motion.button
      data-testid={`banking-role-card-${role.catalog_number}`}
      onClick={onOpen}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
      className="text-left p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <span className={`flex-shrink-0 w-12 h-12 rounded-xl ${v.bg} ${v.text} flex items-center justify-center`}>
          <Icon size={22} />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-bold uppercase tracking-wider ${v.text} mb-0.5`}>#{String(role.catalog_number).padStart(3, '0')} · {role.role_family}</p>
          <h3 className="text-base font-bold text-gray-900 leading-tight">{role.role_name}</h3>
        </div>
        <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${tone.bg} ${tone.text} inline-flex items-center gap-1`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
          {tone.label.replace(' AI RISK', '')}
        </span>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{role.daily_work_preview}</p>
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <span className={`text-[11px] font-semibold ${v.text} inline-flex items-center gap-1`}><Sparkles size={11} />Eligibility, daily work, AI risk &amp; quiz</span>
        <ArrowRight size={14} className={v.text} />
      </div>
    </motion.button>
  );
};
export default CareersAPPage;
